/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, RefreshCw, Download, Filter,
  DollarSign, Percent, CheckCircle2, Activity, BarChart2,
  Users, Receipt, CalendarDays, Package, Building2, Search, X,
} from 'lucide-react';
import {
  AreaChart, Area, ComposedChart, Line, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import CountUp from 'react-countup';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
// import {
//   kpiMetrics, revenueData, productRevenue, topTenantsByRevenue,
//   tenantGrowthData, transactionTrend, filterOptions,
//   recentTransactions, recentInvoices, topProducts,
//   sparklineRevenue, sparklineArr, sparklineTenants, sparklineSuccess,
//   sparklineTxn, sparklineCollection,
// } from '../data/mockDashboardData';
import DateRangePicker from "../components/Common/DateRangePicker";
import { getDashboradChartsDetails } from "../Actions/dashboardChartActions";
import { useEffect } from 'react';
import { decryptedData } from "../utils/encryptDecrypt"
import "./MasterDashboardMUI.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v, symbol = "$") => {
  const n = Number(v);
  if (!Number.isFinite(n)) return `${symbol} 0`;
  if (n >= 1_000_000_000_000) return `${symbol} ${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (n >= 1_000_000_000) return `${symbol} ${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${symbol} ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${symbol} ${(n / 1_000).toFixed(1)}K`;

  return `${symbol} ${n}`;
};

const getPreviousMonthRange = () => {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const firstDay = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
  const lastDay = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);
  const monthName = firstDay.toLocaleString("default", { month: "short" });
  return `vs ${monthName} ${firstDay.getDate()} – ${monthName} ${lastDay.getDate()}`;
};

const KPI_TONES = {
  navy: { accent: "#0e2874", soft: "rgba(14, 40, 116, 0.1)" },
  blue: { accent: "#1d4ed8", soft: "rgba(29, 78, 216, 0.1)" },
  slate: { accent: "#475569", soft: "rgba(71, 85, 105, 0.1)" },
  teal: { accent: "#0f766e", soft: "rgba(15, 118, 110, 0.1)" },
  emerald: { accent: "#047857", soft: "rgba(4, 120, 87, 0.1)" },
  amber: { accent: "#b45309", soft: "rgba(180, 83, 9, 0.1)" },
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({
  title,
  value,
  trend,
  isUp,
  prefix = "",
  suffix = "",
  icon: Icon,
  tone = "navy",
}) => {
  const palette = KPI_TONES[tone] || KPI_TONES.navy;
  const numeric =
    typeof value === "string" ? parseFloat(value) || 0 : Number(value) || 0;
  const decimals =
    suffix === "%"
      ? Number.isInteger(numeric)
        ? 0
        : 1
      : numeric >= 1_000_000
        ? 2
        : 0;

  return (
    <article
      className="mp-kpi-card"
      style={{
        "--kpi-accent": palette.accent,
        "--kpi-soft": palette.soft,
      }}
    >
      <div className="mp-kpi-top">
        <p className="mp-kpi-label">{title}</p>
        <div className="mp-kpi-icon" aria-hidden>
          <Icon size={18} />
        </div>
      </div>

      <p className="mp-kpi-value">
        {prefix}
        <CountUp
          end={numeric}
          decimals={decimals}
          duration={1.2}
          separator=","
        />
        {suffix}
      </p>

      <div className="mp-kpi-meta">
        <span className={`mp-kpi-trend ${isUp ? "is-up" : "is-down"}`}>
          {isUp ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}
          {isUp ? "+" : ""}
          {trend}%
        </span>
        <span className="mp-kpi-compare">{getPreviousMonthRange()}</span>
      </div>
    </article>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionTitle = ({ children, action }) => (
  <div className="mp-section-title">
    <span>{children}</span>
    {action && <button type="button" className="mp-section-action">{action}</button>}
  </div>
);

const CHART_PRIMARY = "#6366f1";
const CHART_PREV = "#cbd5e1";
const PRODUCT_COLORS = {
  paycentral: "#22419e",
  qbot: "#0f766e",
  kyc: "#b45309",
  tripola: "#64748b",
  tripolaa: "#64748b",
};
const productColor = (name = "", index = 0) => {
  const key = String(name).toLowerCase().replace(/\s+/g, "");
  const matched = Object.keys(PRODUCT_COLORS).find((k) => key.includes(k));
  return matched
    ? PRODUCT_COLORS[matched]
    : ["#22419e", "#0f766e", "#b45309", "#64748b"][index % 4];
};

// ─── Card wrapper ─────────────────────────────────────────────────────────────
const Panel = ({ children, style, className = "" }) => (
  <div className={`mp-panel${className ? ` ${className}` : ""}`} style={style}>
    {children}
  </div>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const Badge = ({ status }) => {
  const key = String(status || "pending").toLowerCase();
  const labels = {
    success: "Success",
    paid: "Paid",
    failed: "Failed",
    overdue: "Overdue",
    pending: "Pending",
  };
  return (
    <span className={`mp-badge is-${labels[key] ? key : "pending"}`}>
      {labels[key] || labels.pending}
    </span>
  );
};

const shortRef = (value, max = 10) => {
  if (value == null || value === "") return "—";
  const text = String(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const EmptyPanel = ({ title, hint }) => (
  <div className="mp-empty">
    <strong>{title}</strong>
    <span>{hint}</span>
  </div>
);

const SkeletonBlock = ({ className = "" }) => (
  <div className={`mp-skel ${className}`} aria-hidden />
);

const DashboardSkeleton = () => (
  <div className="mp-dashboard-skeleton" aria-busy="true" aria-live="polite">
    <div className="mp-kpi-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`kpi-a-${i}`} className="mp-skel-kpi">
          <SkeletonBlock className="mp-skel-line is-sm is-w40" />
          <SkeletonBlock className="mp-skel-line is-lg is-w55" />
          <SkeletonBlock className="mp-skel-line is-xs is-w70" />
        </div>
      ))}
    </div>
    <div className="mp-kpi-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`kpi-b-${i}`} className="mp-skel-kpi">
          <SkeletonBlock className="mp-skel-line is-sm is-w40" />
          <SkeletonBlock className="mp-skel-line is-lg is-w55" />
          <SkeletonBlock className="mp-skel-line is-xs is-w70" />
        </div>
      ))}
    </div>

    <div className="mp-skel-charts-row">
      <div className="mp-skel-panel is-wide">
        <SkeletonBlock className="mp-skel-line is-sm is-w35" />
        <SkeletonBlock className="mp-skel-chart" />
      </div>
      <div className="mp-skel-panel">
        <SkeletonBlock className="mp-skel-line is-sm is-w50" />
        <SkeletonBlock className="mp-skel-donut" />
        <SkeletonBlock className="mp-skel-line is-xs is-w80" />
        <SkeletonBlock className="mp-skel-line is-xs is-w70" />
      </div>
      <div className="mp-skel-panel">
        <SkeletonBlock className="mp-skel-line is-sm is-w60" />
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonBlock key={i} className="mp-skel-bar" />
        ))}
      </div>
    </div>

    <div className="mp-skel-secondary-row">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="mp-skel-panel">
          <SkeletonBlock className="mp-skel-line is-sm is-w45" />
          <SkeletonBlock className="mp-skel-line is-lg is-w40" />
          <SkeletonBlock className="mp-skel-chart is-short" />
        </div>
      ))}
    </div>

    <div className="mp-tables-row">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="mp-skel-panel is-flush">
          <div className="mp-skel-table-head">
            <SkeletonBlock className="mp-skel-line is-sm is-w45" />
            <SkeletonBlock className="mp-skel-line is-xs is-w15" />
          </div>
          {Array.from({ length: 5 }).map((__, r) => (
            <SkeletonBlock key={r} className="mp-skel-table-row" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const ChartTip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0f172a', borderRadius: 10, padding: '10px 14px',
      border: '1px solid rgba(255,255,255,0.1)', fontSize: 12, color: '#f8fafc',
    }}>
      <div style={{ marginBottom: 6, fontWeight: 600, color: '#94a3b8' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ color: '#cbd5e1' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{currency ? fmt(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartTipForGrowth = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;

  const month = payload?.[0]?.payload?.month;

  return (
    <div
      style={{
        background: "#0f172a",
        borderRadius: 10,
        padding: "10px 14px",
        border: "1px solid rgba(255,255,255,0.1)",
        fontSize: 12,
        color: "#f8fafc",
      }}
    >
      <div
        style={{
          marginBottom: 6,
          fontWeight: 600,
          color: "#94a3b8",
        }}
      >
        {month}
      </div>

      {payload?.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: p?.color,
            }}
          />
          <span style={{ color: "#cbd5e1" }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>
            {currency ? fmt(p?.value) : p?.value}
          </span>
        </div>
      ))}

      {/* {payload?.[0]?.payload?.newTenants !== undefined && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            color: "#22c55e",
            fontWeight: 600,
          }}
        >
          New Tenants: {payload[0]?.payload?.newTenants}
        </div>
      )} */}
    </div>
  );
};

const convertMonthlyToQuarterly = (
  data
) => {

  const quarters = {};

  data.forEach((item, index) => {

    const quarter =
      `Q${Math.floor(index / 3) + 1}`;

    if (!quarters[quarter]) {
      quarters[quarter] = {
        month: quarter,
        revenue: 0,
        prevYear: 0,
      };
    }

    quarters[quarter].revenue +=
      Number(item.revenue || 0);

    quarters[quarter].prevYear +=
      Number(item.prevYear || 0);
  });

  return Object.values(quarters);
};


const filterOptions = {
  dateRanges: [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 15 Days', value: 'last15days' },
    // { label: 'Last 30 Days', value: 'last30days' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'This Quarter', value: 'thisQuarter' },
    { label: 'Last 6 Months', value: 'last6months' },
    { label: 'Custom Range', value: 'custom' },
  ],
  products: [
    { label: 'QBot Portal', value: 'qbot' },
    { label: 'PayCentral', value: 'paycentral' },
    { label: 'KYC Portal', value: 'kyc' },
    { label: 'Tripolaa Portal', value: 'tripola' },
  ],
};

const DEFAULT_DATE_RANGE = 'last6months';

const FILTER_MENU_PROPS = {
  PaperProps: { className: 'mp-filter-menu-paper' },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const TENANT_MENU_PROPS = {
  ...FILTER_MENU_PROPS,
  autoFocus: false,
  disableAutoFocusItem: true,
  PaperProps: {
    className: 'mp-filter-menu-paper mp-filter-tenant-menu',
  },
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const MasterDashboard = () => {
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const [product, setProduct] = useState('');
  const [tenant, setTenant] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [shouldFetchDetails, setShouldFetchDetails] = useState(true);

  //  dashboard Data
  const [dashbaordData, setDashboardData] = useState({});

  // tenant growth data states
  const [growthData, setGrowthData] = useState([]);
  const [growthDataStates, setGrowthDataStates] = useState([]);

  const [recentTransactionsData, setRecentTransactionsData] = useState([]);
  const [topProductsData, setTopProductsData] = useState([]);

  // revenue trend chart data
  const [revenueTrendData, setRevenueTrendData] = useState([]);
  // revenue by product pie chart data
  const [revenueByProductData, setRevenueByProductData] = useState([]);
  const [totalRevenueByProductCountData, setTotalRevenueByProductCountData] = useState([]);

  // top 10 tenants by revenue data
  const [topTenantsByRevenueData, setTopTenantsByRevenueData] = useState([]);

  // MRR Reports Data
  const [mrrData, setMrrData] = useState([]);

  //Recent Invoices
  const [recentInvoicesData, setRecentInvoicesData] = useState([]);

  //Invoice Status 
  const [invoiceStatusData, setInvoiceStatusData] = useState({});

  //kpicard data
  const [kpiData, setKpiData] = useState({})

  //Revenuetrend graph state 
  const [revenueViewType, setRevenueViewType] = useState("monthly");

  //all tenant data
  const [allTenantsData, setAllTenantsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const filteredTenants = product
    ? allTenantsData.filter(
        (t) =>
          t.portalType?.toLowerCase()?.trim() === product?.toLowerCase()?.trim()
      )
    : allTenantsData;

  const searchedTenants = tenantSearch.trim()
    ? filteredTenants.filter((t) =>
        t.tenantName?.toLowerCase().includes(tenantSearch.trim().toLowerCase())
      )
    : filteredTenants;

  // useEffect(() => {
  //   fetchDashboardData();
  // }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [product, tenant, dateRange, startDate, endDate]);


  const handleResetFilters = () => {
    setDateRange(DEFAULT_DATE_RANGE);
    setProduct("");
    setTenant("");
    setTenantSearch("");
    setStartDate("");
    setEndDate("");
  };

  const handleProductChange = (value) => {
    setProduct(value);
    setTenant("");
    setTenantSearch("");
  };

  const activeFilterCount = [
    dateRange !== DEFAULT_DATE_RANGE,
    Boolean(product),
    Boolean(tenant),
  ].filter(Boolean).length;

  const fetchDashboardData = () => {
    setLoading(true);
    getDashboradChartsDetails(product, tenant, dateRange, startDate, endDate)
      .then((res) => {
        setDashboardData(res?.data || {});
        setGrowthData(res?.data?.data?.tenantGrowthData?.tenantGrowthData || []);
        setGrowthDataStates(res?.data?.data?.tenantGrowthData || []);
        setRecentTransactionsData(res?.data?.data?.recentTransactionData || []);
        setTopProductsData(res?.data?.data?.topProductsRevenueData || []);
        setRevenueTrendData(res?.data?.data?.productRevenueData || []);
        setRevenueByProductData(res?.data?.data?.revenueByProductData?.productRevenue || []);
        setTotalRevenueByProductCountData(res?.data?.data?.revenueByProductData?.totalRevenue || []);
        setTopTenantsByRevenueData(res?.data?.data?.topTenantByRevenueData || []);
        setMrrData(res?.data?.data?.MRROverviewData || {});
        setRecentInvoicesData(res?.data?.data?.RecentInvoices || []);
        setInvoiceStatusData(res?.data?.data?.InvoiceStatusData || {})
        setKpiData(res?.data?.data?.KPIMetricsData || {})
        setAllTenantsData(res?.data?.data?.AllTenantData || []);
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  // console.log(allTenantsData);
  // console.log(product);

  const maxRev = Math.max(
    1,
    ...(topTenantsByRevenueData?.map((item) => Number(item.revenue)) || [0])
  );
  const DONUT_COLORS = ["#22419e", "#0f766e", "#b45309", "#64748b"];

  const revenueChartData =
    revenueViewType === "quarterly"
      ? convertMonthlyToQuarterly(revenueTrendData)
      : revenueTrendData;

  const invoiceChartData =
    invoiceStatusData?.chartData?.length
      ? invoiceStatusData.chartData
      : [
          { name: "Paid", v: 0 },
          { name: "Pending", v: 0 },
          { name: "Overdue", v: 0 },
        ];
  const invoiceStats =
    invoiceStatusData?.stats?.length
      ? invoiceStatusData.stats
      : [
          { color: "#22c55e", label: "Paid", value: "0% (0)" },
          { color: "#f59e0b", label: "Pending", value: "0% (0)" },
          { color: "#ef4444", label: "Overdue", value: "0% (0)" },
        ];
  const invoiceColors = ["#22c55e", "#f59e0b", "#ef4444"];
  const mrrSparkData = mrrData?.revenueData?.slice(-6) || [];

  return (
    <div style={{ minHeight: '100vh' }}>

      {/* ── Page Body ──────────────────────────────────────────────────── */}
      <main style={{ padding: '0px', flex: 1 }}>

        {/* ── Filter Bar ───────────────────────────────────────────────── */}
        <section className="mp-filter-bar" aria-label="Dashboard filters">
          <div className="mp-filter-heading">
            <span className="mp-filter-heading-icon" aria-hidden>
              <Filter size={18} />
            </span>
            <div className="mp-filter-heading-copy">
              <h2>Dashboard filters</h2>
              <p>Focus the overview by period, product, or tenant</p>
            </div>
          </div>

          <div className="mp-filter-controls">
            <div className="mp-filter-field mp-filter-period">
              <span>Reporting period</span>
              <div className={`mp-filter-input ${dateRange !== DEFAULT_DATE_RANGE ? "is-active" : ""}`}>
                <CalendarDays size={17} aria-hidden />
                <Select
                  className="mp-filter-select"
                  variant="standard"
                  disableUnderline
                  value={dateRange}
                  onChange={e => setDateRange(e.target.value)}
                  MenuProps={FILTER_MENU_PROPS}
                >
                  {filterOptions.dateRanges.map(d => (
                    <MenuItem key={d.value} value={d.value} className="mp-filter-menu-item">
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            {dateRange === "custom" && (
              <div className="mp-filter-field mp-filter-range">
                <span>Custom range</span>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={today}
                  onApply={(from, to) => {
                    setStartDate(from);
                    setEndDate(to);
                  }}
                />
              </div>
            )}

            <div className="mp-filter-field mp-filter-product">
              <span>Product</span>
              <div className={`mp-filter-input ${product ? "is-active" : ""}`}>
                <Package size={17} aria-hidden />
                <Select
                  className="mp-filter-select"
                  variant="standard"
                  disableUnderline
                  displayEmpty
                  value={product}
                  onChange={e => handleProductChange(e.target.value)}
                  MenuProps={FILTER_MENU_PROPS}
                >
                  <MenuItem value="" className="mp-filter-menu-item">All Products</MenuItem>
                  {filterOptions.products.map(p => (
                    <MenuItem key={p.value} value={p.value} className="mp-filter-menu-item">
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            <div className="mp-filter-field mp-filter-tenant">
              <span>Tenant</span>
              <div className={`mp-filter-input ${tenant ? "is-active" : ""}`}>
                <Building2 size={17} aria-hidden />
                <Select
                  className="mp-filter-select"
                  variant="standard"
                  disableUnderline
                  displayEmpty
                  value={tenant}
                  onChange={(e) => setTenant(e.target.value)}
                  onClose={() => setTenantSearch("")}
                  MenuProps={TENANT_MENU_PROPS}
                  renderValue={(selected) => {
                    if (!selected) return "All Tenants";
                    const match = filteredTenants.find(
                      (t) => String(t.portalId) === String(selected)
                    );
                    return match?.tenantName || "All Tenants";
                  }}
                >
                  <ListSubheader className="mp-filter-menu-search">
                    <span className="mp-filter-menu-search-box">
                      <Search size={14} aria-hidden />
                      <input
                        type="text"
                        placeholder="Search tenant..."
                        value={tenantSearch}
                        onChange={(e) => setTenantSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                      {tenantSearch && (
                        <button
                          type="button"
                          className="mp-filter-search-clear"
                          aria-label="Clear tenant search"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTenantSearch("");
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <X size={14} aria-hidden />
                        </button>
                      )}
                    </span>
                  </ListSubheader>
                  <MenuItem value="" className="mp-filter-menu-item">
                    All Tenants
                  </MenuItem>
                  {searchedTenants.map((t) => (
                    <MenuItem
                      key={t.portalId}
                      value={t.portalId}
                      className="mp-filter-menu-item"
                    >
                      {t.tenantName}
                    </MenuItem>
                  ))}
                  {searchedTenants.length === 0 && (
                    <MenuItem disabled className="mp-filter-menu-item is-empty">
                      No tenants found
                    </MenuItem>
                  )}
                </Select>
              </div>
            </div>

            <button
              type="button"
              className={`mp-filter-reset ${activeFilterCount ? "is-active" : ""}`}
              onClick={handleResetFilters}
              disabled={!activeFilterCount}
              title={activeFilterCount ? "Reset filters" : "No filters applied"}
            >
              <RefreshCw size={14} aria-hidden />
              Reset filters
              {activeFilterCount > 0 && (
                <span className="mp-filter-reset-count">{activeFilterCount}</span>
              )}
            </button>
          </div>
        </section>

        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
        {/* ── Row 1: KPI Cards ─────────────────────────────────────────── */}
        <div className="mp-kpi-grid">
          <KpiCard
            title="Monthly Revenue"
            value={kpiData?.monthlyRevenue || 0}
            prefix="$"
            trend={kpiData?.monthlyRevenueTrend || 0}
            isUp={(kpiData?.monthlyRevenueTrend || 0) >= 0}
            icon={DollarSign}
            tone="navy"
          />
          <KpiCard
            title="ARR"
            value={kpiData?.arr || 0}
            prefix="$"
            trend={kpiData?.arrTrend || 0}
            isUp={(kpiData?.arrTrend || 0) >= 0}
            icon={TrendingUp}
            tone="blue"
          />
          <KpiCard
            title="Active Tenants"
            value={kpiData?.activeTenants || 0}
            trend={kpiData?.activeTenantsTrend || 0}
            isUp={(kpiData?.activeTenantsTrend || 0) >= 0}
            icon={Users}
            tone="slate"
          />
          <KpiCard
            title="Success Rate"
            value={kpiData?.successRate || 0}
            suffix="%"
            trend={kpiData?.successRateTrend || 0}
            isUp={(kpiData?.successRateTrend || 0) >= 0}
            icon={CheckCircle2}
            tone="emerald"
          />
        </div>

        <div className="mp-kpi-grid">
          <KpiCard
            title="Total Transactions"
            value={kpiData?.totalTransactions || 0}
            trend={kpiData?.totalTransactionsTrend || 0}
            isUp={(kpiData?.totalTransactionsTrend || 0) >= 0}
            icon={Activity}
            tone="teal"
          />
          <KpiCard
            title="Pending Invoices"
            value={kpiData?.pendingInvoices || 0}
            trend={kpiData?.pendingInvoicesTrend || 0}
            isUp={(kpiData?.pendingInvoicesTrend || 0) >= 0}
            icon={Receipt}
            tone="amber"
          />
          <KpiCard
            title="Collection Rate"
            value={kpiData?.collectionRate || 0}
            suffix="%"
            trend={kpiData?.collectionRateTrend || 0}
            isUp={(kpiData?.collectionRateTrend || 0) >= 0}
            icon={Percent}
            tone="emerald"
          />
          <KpiCard
            title="Revenue Growth"
            value={kpiData?.revenueGrowth || 0}
            suffix="%"
            trend={kpiData?.revenueGrowthTrend || 0}
            isUp={(kpiData?.revenueGrowthTrend || 0) >= 0}
            icon={BarChart2}
            tone="navy"
          />
        </div>

        {/* ── Row 2: Primary Charts ─────────────────────────────────────── */}
        <div className="mp-charts-primary">
          <Panel className="mp-chart-revenue">
            <div className="mp-chart-head">
              <div>
                <h3 className="mp-chart-title">
                  Revenue Trend (
                  {revenueViewType === "monthly"
                    ? "Last 12 Months"
                    : "Quarterly"}
                  )
                </h3>
                <div className="mp-chart-legend">
                  <span>
                    <i className="mp-legend-line is-solid" aria-hidden />
                    Revenue
                  </span>
                  <span>
                    <i className="mp-legend-line is-dashed" aria-hidden />
                    Previous Year
                  </span>
                </div>
              </div>
              <select
                className="mp-chart-select"
                value={revenueViewType}
                onChange={(e) => setRevenueViewType(e.target.value)}
                aria-label="Revenue view"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div className="mp-chart-body is-trend">
              {!revenueChartData?.length ? (
                <EmptyPanel
                  title="No revenue data"
                  hint="Try another period or product filter."
                />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart
                    data={revenueChartData}
                    margin={{ top: 12, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor={CHART_PRIMARY}
                          stopOpacity={0.2}
                        />
                        <stop
                          offset="95%"
                          stopColor={CHART_PRIMARY}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#eef2f7"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                      dy={6}
                    />
                    <YAxis
                      tickFormatter={(v) => fmt(v)}
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                      width={58}
                    />
                    <Tooltip content={<ChartTip currency />} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke={CHART_PRIMARY}
                      strokeWidth={2.5}
                      fill="url(#gRev)"
                      dot={false}
                      activeDot={{
                        r: 5,
                        fill: CHART_PRIMARY,
                        stroke: "#fff",
                        strokeWidth: 2,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="prevYear"
                      name="Previous Year"
                      stroke={CHART_PREV}
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                      activeDot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </Panel>

          <Panel className="mp-chart-donut-panel">
            <SectionTitle>Revenue by Product</SectionTitle>
            <div className="mp-donut-wrap">
              {!revenueByProductData?.length ? (
                <EmptyPanel
                  title="No product revenue"
                  hint="Revenue by portal will appear here."
                />
              ) : (
                <>
                  <div className="mp-donut-chart">
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={revenueByProductData}
                          cx="50%"
                          cy="50%"
                          innerRadius={52}
                          outerRadius={72}
                          paddingAngle={2}
                          dataKey="revenue"
                          nameKey="product"
                          startAngle={90}
                          endAngle={-270}
                          stroke="none"
                        >
                          {revenueByProductData.map((p, i) => (
                            <Cell
                              key={p.product || i}
                              fill={productColor(p.product, i)}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          content={<ChartTip currency />}
                          cursor={false}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mp-donut-center" aria-hidden>
                      <strong>{fmt(totalRevenueByProductCountData)}</strong>
                      <span>Total</span>
                    </div>
                  </div>
                  <ul className="mp-donut-legend">
                    {revenueByProductData.map((p, i) => (
                      <li key={p.product || i}>
                        <span className="mp-donut-legend-label">
                          <i
                            style={{
                              background: productColor(p.product, i),
                            }}
                          />
                          {p.product}
                        </span>
                        <span className="mp-donut-legend-value">
                          {p.percentage}% ({fmt(p.revenue)})
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </Panel>

          <Panel className="mp-chart-tenants-panel">
            <SectionTitle>Top 10 Tenants by Revenue</SectionTitle>
            {!topTenantsByRevenueData?.length ? (
              <EmptyPanel
                title="No tenant revenue"
                hint="Top tenants will list here once invoices clear."
              />
            ) : (
              <ul className="mp-tenant-bars">
                {topTenantsByRevenueData.map((t) => (
                  <li key={t.tenantName}>
                    <div className="mp-tenant-bars-meta">
                      <span title={t.tenantName}>{t.tenantName}</span>
                      <strong>{fmt(t.revenue)}</strong>
                    </div>
                    <div className="mp-tenant-bars-track">
                      <div
                        className="mp-tenant-bars-fill"
                        style={{
                          width: `${Math.max(
                            4,
                            (Number(t.revenue) / maxRev) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        {/* ── Row 3: Secondary Analytics ───────────────────────────────── */}
        <div className="mp-charts-secondary">
          <Panel className="mp-metric-spark">
            <SectionTitle>Tenant Growth</SectionTitle>
            <div className="mp-metric-value">
              {growthDataStates?.totalTenants ?? 0}
            </div>
            <div className="mp-metric-label">Total Tenants</div>
            <div className="mp-metric-delta is-up">
              ↑ {growthDataStates?.currentMonthNewTenants ?? 0} this month
            </div>
            <div className="mp-spark">
              <ResponsiveContainer width="100%" height={72}>
                <AreaChart
                  data={growthData}
                  margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gTen" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={CHART_PRIMARY}
                        stopOpacity={0.22}
                      />
                      <stop
                        offset="95%"
                        stopColor={CHART_PRIMARY}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<ChartTipForGrowth />} />
                  <Area
                    type="monotone"
                    dataKey="tenants"
                    name="Tenants"
                    stroke={CHART_PRIMARY}
                    strokeWidth={2.5}
                    fill="url(#gTen)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel>
            <SectionTitle>Invoice Status</SectionTitle>
            <div className="mp-invoice-status">
              <div className="mp-invoice-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={invoiceChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={34}
                      outerRadius={52}
                      paddingAngle={2}
                      startAngle={90}
                      endAngle={-270}
                      dataKey="v"
                      nameKey="name"
                      stroke="none"
                    >
                      {invoiceChartData.map((entry, i) => (
                        <Cell
                          key={entry.name || i}
                          fill={invoiceColors[i % invoiceColors.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mp-invoice-legend">
                {invoiceStats.map((row) => (
                  <li key={row.label}>
                    <span className="mp-donut-legend-label">
                      <i style={{ background: row.color }} />
                      {row.label}
                    </span>
                    <strong>{row.value}</strong>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>

          <Panel className="mp-metric-spark">
            <SectionTitle>MRR Overview</SectionTitle>
            <div className="mp-metric-value is-money">
              {fmt(mrrData?.totalRevenue || 0)}
            </div>
            <div className="mp-metric-label">Monthly Recurring Revenue</div>
            <div
              className={`mp-metric-delta ${
                mrrData?.isGrowthPositive ? "is-up" : "is-down"
              }`}
            >
              {mrrData?.isGrowthPositive ? "↑" : "↓"}{" "}
              {mrrData?.growthPercentage ?? 0}% vs last month
            </div>
            <div className="mp-spark">
              <ResponsiveContainer width="100%" height={72}>
                <AreaChart
                  data={mrrSparkData}
                  margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gMrr" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={CHART_PRIMARY}
                        stopOpacity={0.22}
                      />
                      <stop
                        offset="95%"
                        stopColor={CHART_PRIMARY}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<ChartTipForGrowth currency />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name="MRR"
                    stroke={CHART_PRIMARY}
                    strokeWidth={2.5}
                    fill="url(#gMrr)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* ── Row 4: Tables ────────────────────────────────────────────── */}
        <div className="mp-tables-row">
          <Panel className="is-flush">
            <div className="mp-panel-head">
              <div className="mp-panel-head-copy">
                <h3>Recent Transactions</h3>
                <p>Latest payments across assigned portals</p>
              </div>
              <span className="mp-panel-count">
                {recentTransactionsData?.length || 0}
              </span>
            </div>
            {!recentTransactionsData?.length ? (
              <EmptyPanel
                title="No recent transactions"
                hint="New payments will show up here as they clear."
              />
            ) : (
              <div className="mp-table-wrap">
                <table className="mp-data-table">
                  <thead>
                    <tr>
                      {["ID", "Tenant", "Product", "Amount", "Status", "Date"].map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactionsData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="mp-cell-id" title={String(row.id)}>
                            {shortRef(row.id)}
                          </span>
                        </td>
                        <td>
                          <span className="mp-cell-name" title={row.tenant}>
                            {row.tenant || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="mp-cell-muted">{row.product || "—"}</span>
                        </td>
                        <td>
                          <span className="mp-cell-money">{fmt(row.amount)}</span>
                        </td>
                        <td>
                          <Badge status={row.status} />
                        </td>
                        <td>
                          <span className="mp-cell-muted">{row.date || "—"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel className="is-flush">
            <div className="mp-panel-head">
              <div className="mp-panel-head-copy">
                <h3>Recent Invoices</h3>
                <p>Billing activity from the selected period</p>
              </div>
              <span className="mp-panel-count">
                {recentInvoicesData?.length || 0}
              </span>
            </div>
            {!recentInvoicesData?.length ? (
              <EmptyPanel
                title="No recent invoices"
                hint="Issued invoices for this period will appear here."
              />
            ) : (
              <div className="mp-table-wrap">
                <table className="mp-data-table">
                  <thead>
                    <tr>
                      {["Invoice #", "Tenant", "Product", "Amount", "Status", "Due Date"].map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentInvoicesData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span
                            className="mp-cell-id"
                            title={String(row.invoiceNumber || row.id)}
                          >
                            {shortRef(row.invoiceNumber || row.id)}
                          </span>
                        </td>
                        <td>
                          <span className="mp-cell-name" title={row.tenant}>
                            {row.tenant || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="mp-cell-muted">{row.product || "—"}</span>
                        </td>
                        <td>
                          <span className="mp-cell-money">{fmt(row.amount)}</span>
                        </td>
                        <td>
                          <Badge status={row.status} />
                        </td>
                        <td>
                          <span className="mp-cell-muted">
                            {row.dueDate || "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel className="is-flush">
            <div className="mp-panel-head">
              <div className="mp-panel-head-copy">
                <h3>Top Products by Revenue</h3>
                <p>Share of portal revenue this period</p>
              </div>
              <span className="mp-panel-count">
                {topProductsData?.length || 0}
              </span>
            </div>
            {!topProductsData?.length ? (
              <EmptyPanel
                title="No product revenue"
                hint="Product share will appear once revenue is recorded."
              />
            ) : (
              <div className="mp-table-wrap">
                <table className="mp-data-table">
                  <thead>
                    <tr>
                      {["Product", "Revenue", "% Share", "Growth"].map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topProductsData.map((p, i) => {
                      const growth = Number(p.growth) || 0;
                      const share = Math.max(0, Math.min(100, Number(p.share) || 0));
                      const isUp = growth >= 0;
                      return (
                        <tr key={p.name || i}>
                          <td>
                            <div className="mp-product-cell">
                              <span
                                className="mp-product-dot"
                                style={{
                                  background: DONUT_COLORS[i % DONUT_COLORS.length],
                                }}
                              />
                              <div className="mp-product-meta">
                                <strong>{p.name}</strong>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="mp-cell-money">
                              {fmt(p.revenue)}
                            </span>
                          </td>
                          <td>
                            <div className="mp-share">
                              <span className="mp-share-label">{share}%</span>
                              <div className="mp-share-track">
                                <div
                                  className="mp-share-fill"
                                  style={{
                                    width: `${share}%`,
                                    background: DONUT_COLORS[i % DONUT_COLORS.length],
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`mp-growth ${isUp ? "is-up" : "is-down"}`}>
                              {isUp ? "↑" : "↓"} {Math.abs(growth)}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
          </>
        )}
      </main>
    </div>
  );
};

export default MasterDashboard;
